import { stopSegmentStates } from "@/lib/routeCostCalculation"
import { getRouteConcernScore, getRouteStatusDisplay } from "@/lib/statusDisplay"
import type { Variance } from "@/lib/tone"
import type { Route } from "@/types/collections"

export function routeHasEmptyLeg(route: Route): boolean {
  return stopSegmentStates(route.stops || []).includes("empty")
}

export interface MonitoringRouteFilters {
  searchTerm: string
  /** Route statuses, plus the two view toggles `empty_leg` and `hide_standby`. */
  statusFilter: string[]
  depotFilter: string[]
  bandFilter: string | null
}

export interface MonitoringRouteSignals {
  currentTime: Date
  /** The smoothed variance band, the same one the stat band counts. */
  varianceOf: (route: Route) => Variance | null
  loadFactors: Map<string, number>
}

export function filterMonitoringRoutes(
  routes: Route[],
  { searchTerm, statusFilter, depotFilter, bandFilter }: MonitoringRouteFilters,
  { currentTime, varianceOf, loadFactors }: MonitoringRouteSignals
): Route[] {
  const statusValues = statusFilter.filter((s) => s !== "empty_leg" && s !== "hide_standby")
  const requireEmptyLeg = statusFilter.includes("empty_leg")
  const hideStandby = statusFilter.includes("hide_standby")
  return routes.filter((route) => {
    const term = searchTerm.toLowerCase()
    const matchesSearch =
      route.code.toLowerCase().includes(term) ||
      route.driverName.toLowerCase().includes(term) ||
      route.vehicleReg.toLowerCase().includes(term) ||
      route.stops.some(
        (s) =>
          s.name.toLowerCase().includes(term) ||
          s.address.toLowerCase().includes(term) ||
          s.postcode.toLowerCase().includes(term)
      )
    const matchesStatus = statusValues.length === 0 || statusValues.includes(route.status)
    const matchesDepot = depotFilter.length === 0 || depotFilter.includes(route.depot)
    const matchesEmptyLeg = !requireEmptyLeg || routeHasEmptyLeg(route)

    let matchesBand = true
    if (bandFilter) {
      const loadFactor = loadFactors.get(route.id)
      if (bandFilter === "alarm") {
        const band = varianceOf(route)
        matchesBand = band === "at_risk" || band === "late"
      } else if (bandFilter === "over_capacity") {
        matchesBand = loadFactor !== undefined && loadFactor >= 100
      } else {
        const { key } = getRouteStatusDisplay(route, currentTime)
        // "late" covers both a route running behind and one that never left on time.
        matchesBand =
          bandFilter === "late" ? key === "late" || key === "late_start" : key === bandFilter
      }
    }

    // Hiding stand-by is a view preference, not a claim the day has no spares.
    const matchesKind = !hideStandby || route.routeKind !== "standby"
    return (
      matchesSearch &&
      matchesStatus &&
      matchesDepot &&
      matchesEmptyLeg &&
      matchesBand &&
      matchesKind
    )
  })
}

export function sortMonitoringRoutes(
  routes: Route[],
  sortBy: string,
  currentTime: Date,
  loadFactors: Map<string, number>
): Route[] {
  const sorted = [...routes]
  switch (sortBy) {
    case "concern":
      return sorted.sort((a, b) => {
        const scoreA = getRouteConcernScore(a, currentTime, loadFactors.get(a.id))
        const scoreB = getRouteConcernScore(b, currentTime, loadFactors.get(b.id))
        return scoreB - scoreA
      })
    case "most_behind":
      return sorted.sort((a, b) => (b.delayMinutes || 0) - (a.delayMinutes || 0))
    case "most_ahead":
      return sorted.sort((a, b) => (a.delayMinutes || 0) - (b.delayMinutes || 0))
    case "route_code":
      return sorted.sort((a, b) => a.code.localeCompare(b.code))
    case "driver_name":
      return sorted.sort((a, b) => a.driverName.localeCompare(b.driverName))
    case "start_time":
      return sorted.sort((a, b) => a.plannedStartTime.getTime() - b.plannedStartTime.getTime())
    case "progress":
      return sorted.sort((a, b) => {
        const aProgress = a.totalStops > 0 ? a.completedStops / a.totalStops : 0
        const bProgress = b.totalStops > 0 ? b.completedStops / b.totalStops : 0
        return bProgress - aProgress
      })
    default:
      return sorted
  }
}

/** Stand-by rows sit below every real run whatever the sort; a stable partition, as in planning. */
export function standbyLast(routes: Route[]): Route[] {
  const standby = routes.filter((route) => route.routeKind === "standby")
  if (standby.length === 0) return routes
  return [...routes.filter((route) => route.routeKind !== "standby"), ...standby]
}
